import { RolesApi, UsersApi } from '@api';
import { Card, Box, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { FormController, RenderFormElement } from '@components/form';
import { UsersType } from '@types';
import { CheckBoxGroup, OverlayDialog, SwitchOnOff, toasterApi } from '@ui';
import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

interface Props {
  user: UsersType.UserPartial;
}

const Roles: React.FC<Props> = ({ user }) => {
  const [enableEdit, setEnableEdit] = useState(false);
  const [patchUser] = UsersApi.Api.usePatchUserMutation();
  const { data: roles, refetch } = RolesApi.Api.useGetAllRolesQuery();

  const initialRoleIds = useMemo(
    () =>
      (user?.roles ?? [])
        .map((r) => r.id)
        .filter((id): id is number => id !== undefined)
        .sort((a, b) => a - b),
    [user],
  );

  const [selectedRoles, setSelectedRoles] = useState<number[]>(initialRoleIds);

  useEffect(() => {
    setSelectedRoles(initialRoleIds);
  }, [initialRoleIds]);

  const onFormSubmit = async () => {
    const formRoles = [...selectedRoles].sort((a, b) => a - b);
    const userRoles = [...initialRoleIds].sort((a, b) => a - b);

    if (!formRoles.length) {
      toasterApi({
        type: 'warning',
        message: 'Please select at least one role!',
      });
      return;
    }

    const hasChanges =
      formRoles.length !== userRoles.length ||
      formRoles.some((id, index) => id !== userRoles[index]);

    if (!hasChanges) {
      toasterApi({
        type: 'warning',
        message: 'No changes to update',
      });
      return;
    }

    OverlayDialog.open('confirmUserRolesUpdate', {
      overlayId: 'confirmUserRolesUpdate',
      title: 'Confirm User Roles Update',
      description: 'Are you sure you want to update the user roles?',
      content: (
        <VStack align="flex-start" gap={2}>
          <Text>This will update the user&apos;s roles.</Text>
          <RenderFormElement
            previousValue={user?.roles?.map((r) => r.name).join(', ')}
            currentValue={formRoles
              .map((roleId) => roles?.find((role) => role.id === roleId)?.name)
              .filter(Boolean)
              .join(', ')}
            label="Roles"
          />
        </VStack>
      ),
      handleConfirm: async () => {
        const result = await patchUser({
          userId: user.id!,
          args: {
            roles: formRoles,
          },
        });

        if ('data' in result && result.data) {
          toasterApi({
            type: 'success',
            message: 'User roles updated successfully',
          });

          setEnableEdit(false);
          await refetch();
          return;
        }

        toasterApi({
          type: 'error',
          message: 'Failed to update user roles',
        });
      },
    });
  };

  return (
    <Stack>
      <HStack alignSelf="flex-end" gap={8}>
        <Text textStyle="heading.card" color="tertiary.fg">
          Edit User Roles
        </Text>
        <SwitchOnOff value={enableEdit} onValueChange={setEnableEdit} />
      </HStack>

      <Card.Root width="100%" variant="elevated">
        <Card.Body gap="7">
          <FormController
            key={`form-user-roles-${user.id}-${enableEdit}-${initialRoleIds.join('-')}`}
            label="User Roles"
            formSchema={formSchemaPatch}
            helperText={
              !enableEdit
                ? 'Please Enable Edit Mode'
                : 'Please fill out the form to change the user roles'
            }
            onFormSubmit={onFormSubmit}
            disabled={!enableEdit}
            defaultValues={{
              roles: initialRoleIds,
            }}
            buttonProps={{
              variant: 'outline',
              color: 'tertiary.fg',
              width: '100%',
              mt: '4',
            }}
          >
            {() => (
              <VStack align="stretch" gap={4}>
                <Box pt={3}>
                  <CheckBoxGroup
                    label="Permissible Roles"
                    infoTip="Select all roles to apply to the user"
                    items={roles ?? []}
                    keyForDisplay="name"
                    keyForValue="id"
                    multiple={true}
                    useSelectAllOption={false}
                    layout="wrap"
                    disabled={!enableEdit}
                    defaultValue={selectedRoles.map(String)}
                    onSelect={(values: string[]) => {
                      setSelectedRoles(values.map((v) => Number(v)));
                    }}
                    itemContainerProps={{ px: 3, py: 1 }}
                    containerProps={{
                      pt: 2,
                      overflow: 'visible',
                    }}
                    labelProps={{
                      mb: 2,
                      lineHeight: '1.4',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                    }}
                  />
                </Box>

                {enableEdit && selectedRoles.length === 0 && (
                  <Text color="red.300" fontSize="sm">
                    Please select at least one role
                  </Text>
                )}
              </VStack>
            )}
          </FormController>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

export default Roles;

const formSchemaPatch = z.object({
  roles: z.array(z.number()).min(1, 'Please select at least one role'),
});